<template>
    <!-- Header -->
    <div class="flex justify-between items-center pb-3">
        <h4 class="font-bold mb-0">{{ t('resources.title') }}</h4>
        <Button :icon="viewMode === 'table' ? 'fa-solid fa-grip' : 'fa-solid fa-table'" @click="toggleView" outlined />
    </div>

    <div class="pb-4">
        <!-- Card View -->
        <ResourceCardView
            v-if="viewMode === 'card'"
            :resources="resources"
            :loading="loading"
            :search="cardSearch"
            :status-filter="cardStatusFilter"
            :first="cardFirst"
            :rows="cardPageSize"
            :total-records="cardMetadata?.filtered || 0"
            @search="onCardSearch"
            @status-change="onCardStatusChange"
            @page="onCardPage"
            @create="resourceModal.open('create')"
            @edit="resourceModal.open('edit', $event)"
            @delete="resourceModal.open('delete', $event)"
        />

        <!-- Table View -->
        <ResourceTableView
            v-else
            :resources="resources"
            :loading="loading"
            :filters="tableFilters"
            :sort-field="tableSortField"
            :sort-order="tableSortOrder"
            :first="tableFirst"
            :rows="tablePageSize"
            :total-records="tableMetadata?.filtered || 0"
            @page="onTablePage"
            @filter="onTableFilter"
            @sort="onTableSort"
            @clear-filters="clearTableFilters"
            @create="resourceModal.open('create')"
            @edit="resourceModal.open('edit', $event)"
            @delete="resourceModal.open('delete', $event)"
        />

        <!-- Modals -->
        <ResourceModal v-if="resourceModal.isOpen.value" :visible="resourceModal.isOpen.value" :mode="resourceModal.mode.value" :resource="resourceModal.data.value" @close="resourceModal.close" @saved="onResourceSaved" />
    </div>
</template>

<script setup lang="ts">
import ResourceModal from '@/components/modal/Resource.vue';
import ResourceCardView from '@/components/resources/ResourceCardView.vue';
import ResourceTableView from '@/components/resources/ResourceTableView.vue';
import { useModal } from '@/composables/useModal';
import { useResourcesPage } from '@/composables/useResourcesPage';
import type { Resource } from '@/lib/types/resource';
import { getInitialViewMode, ViewModeKey } from '@/utils/datatable';
import { useToast } from 'primevue/usetoast';
import { onMounted } from 'vue';
import { useI18n } from 'vue-i18n';

const { t } = useI18n();
const toast = useToast();

// Modals
const resourceModal = useModal<Resource>();

// Page logic - initialize with saved preference or responsive default
const {
    // State
    viewMode,
    loading,
    resources,

    // Table
    tableFilters,
    tableSortField,
    tableSortOrder,
    tableFirst,
    tablePageSize,
    tableMetadata,

    // Card
    cardSearch,
    cardStatusFilter,
    cardFirst,
    cardPageSize,
    cardMetadata,

    // Methods
    toggleView,
    fetchData,
    initialize,
    onTablePage,
    onTableFilter,
    onTableSort,
    clearTableFilters,
    onCardPage,
    filterCards
} = useResourcesPage(getInitialViewMode(ViewModeKey.RESOURCES));

// Card view handlers with debouncing
let searchDebounceTimer: ReturnType<typeof setTimeout> | null = null;

function onCardSearch(value: string) {
    cardSearch.value = value;

    if (searchDebounceTimer) {
        clearTimeout(searchDebounceTimer);
    }

    searchDebounceTimer = setTimeout(() => {
        filterCards();
    }, 500);
}

function onCardStatusChange(value: boolean | null) {
    cardStatusFilter.value = value;
    filterCards();
}

// Event handlers
function onResourceSaved() {
    fetchData();
    toast.add({
        severity: 'success',
        summary: t('common.success'),
        detail: t('resources.saveSuccess'),
        life: 3000
    });
}

// Lifecycle
onMounted(async () => {
    await initialize();
});
</script>
